import React, { useState } from 'react';
import { Badge, Popover, List, Button, Typography, Empty, Spin, Grid, Drawer, Tag, Switch, Divider } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNotifications, NotificationItem } from '../context/NotificationContext';
import { usePushNotifications } from '../hooks/usePushNotifications';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { useBreakpoint } = Grid;

const TYPE_COLORS: Record<string, string> = {
  MR_SUBMITTED: 'blue',
  MR_RECALLED: 'orange',
  MR_APPROVED: 'green',
  MR_REJECTED: 'red',
  MR_PARTIALLY_APPROVED: 'orange',
  PO_CREATED: 'purple',
  GRN_RECEIVED: 'cyan',
  PO_LINE_CANCEL_REQUESTED: 'orange',
  PO_LINE_CANCEL_APPROVED: 'red',
  PO_LINE_CANCEL_REJECTED: 'green',
  MR_LINE_CANCEL_REQUESTED: 'orange',
  MR_LINE_CANCEL_APPROVED: 'red',
  MR_LINE_CANCEL_REJECTED: 'green',
};

const TYPE_LABELS: Record<string, string> = {
  MR_SUBMITTED: 'MR Submitted',
  MR_RECALLED: 'MR Recalled',
  MR_APPROVED: 'Approved',
  MR_REJECTED: 'Rejected',
  MR_PARTIALLY_APPROVED: 'Partial',
  PO_CREATED: 'PO Created',
  GRN_RECEIVED: 'GRN Received',
  PO_LINE_CANCEL_REQUESTED: 'Line Cancel',
  PO_LINE_CANCEL_APPROVED: 'Line Cancelled',
  PO_LINE_CANCEL_REJECTED: 'Line Kept',
  MR_LINE_CANCEL_REQUESTED: 'MR Line Cancel',
  MR_LINE_CANCEL_APPROVED: 'MR Line Cancelled',
  MR_LINE_CANCEL_REJECTED: 'MR Line Kept',
};

const NotificationListContent: React.FC = () => {
  const { notifications, unreadCount, loading, fetchMore, markRead, markAllRead, hasMore } = useNotifications();
  const navigate = useNavigate();

  const handleClick = (item: NotificationItem) => {
    if (!item.isRead) markRead(item.id);
    if (item.linkUrl) navigate(item.linkUrl);
  };

  return (
    <div style={{ width: 360, maxHeight: 450, overflow: 'auto' }}>
      {unreadCount > 0 && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>{unreadCount} unread</Text>
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={markAllRead}>
            Mark all read
          </Button>
        </div>
      )}
      {notifications.length === 0 && !loading ? (
        <Empty description="No notifications" style={{ padding: 24 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={notifications}
          loading={loading}
          renderItem={(item: NotificationItem) => (
            <List.Item
              onClick={() => handleClick(item)}
              style={{
                cursor: item.linkUrl ? 'pointer' : 'default',
                padding: '10px 16px',
                background: item.isRead ? 'transparent' : '#f0f5ff',
              }}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag color={TYPE_COLORS[item.type] || 'default'} style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>
                      {TYPE_LABELS[item.type] || item.type}
                    </Tag>
                    {!item.isRead && <Badge status="processing" />}
                  </div>
                }
                description={
                  <div>
                    <Text style={{ fontSize: 13 }}>{item.message}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(item.createdAt).fromNow()}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
      {hasMore && (
        <div style={{ textAlign: 'center', padding: 8 }}>
          <Button type="link" size="small" onClick={fetchMore} loading={loading}>
            Load more
          </Button>
        </div>
      )}
      <PushToggle />
    </div>
  );
};

const PushToggle: React.FC = () => {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();

  if (!isSupported) return null;

  const denied = permission === 'denied';

  return (
    <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Text style={{ fontSize: 12 }}>
        {denied ? 'Push blocked by browser' : 'Push notifications'}
      </Text>
      <Switch
        size="small"
        checked={isSubscribed}
        loading={loading}
        disabled={denied}
        onChange={(checked) => (checked ? subscribe() : unsubscribe())}
      />
    </div>
  );
};

const NotificationBell: React.FC = () => {
  const { unreadCount } = useNotifications();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <BellOutlined
            style={{ fontSize: 18, cursor: 'pointer' }}
            onClick={() => setDrawerOpen(true)}
          />
        </Badge>
        <Drawer
          title="Notifications"
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width="100%"
          bodyStyle={{ padding: 0 }}
        >
          <NotificationListContent />
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      content={<NotificationListContent />}
      trigger="click"
      placement="bottomRight"
      arrow={false}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;
